/**
 * Page "Mes supports de cours" — formateur PWA.
 * Permet d'uploader des PDF / Word, lister, renommer, supprimer.
 */
import { useMemo, useRef, useState } from 'react';
import {
  Upload, FileText, FileType2, Trash2, Edit2, Loader2, Plus,
  X, BookOpen, GraduationCap, Eye,
} from 'lucide-react';
import { FormateurLayout } from '../FormateurLayout';
import {
  useFormateurMeQuery,
  useListerMesSupportsQuery,
  useUploaderSupportMutation,
  useModifierSupportMutation,
  useSupprimerSupportMutation,
  type SupportCoursItem,
  type FormateurMatiere,
} from '../../services/formateurApi';

const TAILLE_MAX_MB = 25;
const EXTS_OK = ['pdf', 'doc', 'docx', 'odt', 'rtf'];

function getExt(name: string): string {
  return (name.split('.').pop() || '').toLowerCase();
}

export default function Supports() {
  const { data: meData } = useFormateurMeQuery();
  const { data, isLoading, refetch } = useListerMesSupportsQuery();
  const [supprimer] = useSupprimerSupportMutation();
  const [modifier] = useModifierSupportMutation();
  const [composing, setComposing] = useState(false);
  const [composingMatiereId, setComposingMatiereId] = useState<number | null>(null);
  const [editing, setEditing] = useState<SupportCoursItem | null>(null);

  const supports = data?.results || [];

  // Group par matière
  const groupes = useMemo(() => {
    const map = new Map<string, { matiere_id: number | null; matiere_libelle: string; niveau: string; semestre: number | null; items: SupportCoursItem[] }>();
    for (const s of supports) {
      const k = `${s.matiere_id || 0}|${s.matiere_libelle}`;
      if (!map.has(k)) {
        map.set(k, {
          matiere_id: s.matiere_id,
          matiere_libelle: s.matiere_libelle || 'Sans matière',
          niveau: s.niveau,
          semestre: s.semestre,
          items: [],
        });
      }
      map.get(k)!.items.push(s);
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.semestre || 0) - (b.semestre || 0)
      || a.matiere_libelle.localeCompare(b.matiere_libelle),
    );
  }, [supports]);

  const handleDelete = async (s: SupportCoursItem) => {
    if (!window.confirm(`Supprimer le support « ${s.titre} » ?`)) return;
    try {
      await supprimer(s.id).unwrap();
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  const handleEditTitre = async (s: SupportCoursItem, nouveauTitre: string, nouvelleDesc: string) => {
    try {
      await modifier({ id: s.id, titre: nouveauTitre, description: nouvelleDesc }).unwrap();
      setEditing(null);
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  return (
    <FormateurLayout title="Mes supports de cours">
      <button onClick={() => setComposing(true)}
        className="w-full px-4 py-3 mb-4 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg inline-flex items-center justify-center gap-2">
        <Plus size={16} /> Ajouter un support
      </button>

      {isLoading && (
        <div className="flex items-center justify-center gap-2 py-10 text-gray-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Chargement…
        </div>
      )}

      {!isLoading && supports.length === 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
          <FileText size={36} strokeWidth={1.3} className="mx-auto mb-3 text-gray-500" />
          <div className="text-sm font-medium text-gray-300">Aucun support encore</div>
          <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">
            Uploadez vos PDF ou documents Word — vos étudiants y accèderont depuis leur portail.
          </p>
        </div>
      )}

      {groupes.length > 0 && (
        <div className="space-y-3">
          {groupes.map(g => (
            <section key={`${g.matiere_id}-${g.matiere_libelle}`} className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-xl overflow-hidden shadow-sm dark:shadow-none">
              <header className="bg-blue-50 dark:bg-blue-900/30 px-3 py-2 border-b border-blue-200 dark:border-blue-800/50 flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-blue-900 dark:text-blue-100 flex items-center gap-2">
                    <BookOpen size={13} /> {g.matiere_libelle}
                  </div>
                  <div className="text-[10px] text-blue-700/80 dark:text-blue-300/70 mt-0.5 flex items-center gap-2">
                    {g.niveau && <span>{g.niveau}</span>}
                    {g.semestre && <span>• S{g.semestre}</span>}
                    <span>• {g.items.length} support{g.items.length > 1 ? 's' : ''}</span>
                  </div>
                </div>
                {g.matiere_id && (
                  <button
                    onClick={() => { setComposingMatiereId(g.matiere_id); setComposing(true); }}
                    className="px-2 py-1 text-[10px] font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded inline-flex items-center gap-1 flex-shrink-0 shadow-sm"
                    title="Ajouter un support à cette matière">
                    <Plus size={10} /> Ajouter
                  </button>
                )}
              </header>
              <ul className="divide-y divide-gray-800">
                {g.items.map(s => (
                  <li key={s.id} className="px-3 py-2.5 flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      s.type_fichier === 'pdf'
                        ? 'bg-red-900/40 text-red-300'
                        : 'bg-blue-900/40 text-blue-300'
                    }`}>
                      {s.type_fichier === 'pdf' ? <FileType2 size={18} /> : <FileText size={18} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-100 truncate">{s.titre}</div>
                      {s.description && (
                        <div className="text-[11px] text-gray-400 truncate mt-0.5">{s.description}</div>
                      )}
                      <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                        <span>{s.nom_original}</span>
                        <span>• {s.taille_humaine}</span>
                        {s.nb_telechargements > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-emerald-400">
                            <Eye size={9} /> {s.nb_telechargements}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {s.fichier_url && (
                        <a href={s.fichier_url} target="_blank" rel="noopener noreferrer"
                          className="p-1.5 text-gray-400 hover:text-emerald-400 hover:bg-gray-800 rounded"
                          title="Ouvrir / Télécharger">
                          <Eye size={13} />
                        </a>
                      )}
                      <button onClick={() => setEditing(s)}
                        className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-gray-800 rounded"
                        title="Modifier le titre ou la description">
                        <Edit2 size={13} />
                      </button>
                      <button onClick={() => handleDelete(s)}
                        className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded"
                        title="Supprimer">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {composing && meData && (
        <UploadModal
          matieres={meData.matieres}
          presetMatiereId={composingMatiereId}
          onClose={() => { setComposing(false); setComposingMatiereId(null); }}
          onUploaded={() => { setComposing(false); setComposingMatiereId(null); refetch(); }}
        />
      )}

      {editing && (
        <EditModal
          item={editing}
          onClose={() => setEditing(null)}
          onSave={(titre, desc) => handleEditTitre(editing, titre, desc)}
        />
      )}
    </FormateurLayout>
  );
}

// ──────────────────────────────────────────────────────────────
// Modal d'upload
// ──────────────────────────────────────────────────────────────

function UploadModal({ matieres, presetMatiereId, onClose, onUploaded }: {
  matieres: FormateurMatiere[];
  presetMatiereId?: number | null;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [matiereId, setMatiereId] = useState<number | ''>(presetMatiereId || matieres[0]?.matiere_id || '');
  const [fichier, setFichier] = useState<File | null>(null);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploader, { isLoading }] = useUploaderSupportMutation();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    const f = e.target.files?.[0];
    if (!f) { setFichier(null); return; }
    const ext = getExt(f.name);
    if (!EXTS_OK.includes(ext)) {
      setError(`Extension .${ext} non autorisée. Formats acceptés : ${EXTS_OK.join(', ')}.`);
      setFichier(null); return;
    }
    if (f.size > TAILLE_MAX_MB * 1024 * 1024) {
      setError(`Fichier trop volumineux (${(f.size / 1024 / 1024).toFixed(1)} Mo). Maximum ${TAILLE_MAX_MB} Mo.`);
      setFichier(null); return;
    }
    setFichier(f);
    // Auto-remplir titre depuis nom de fichier si vide
    if (!titre) setTitre(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!fichier) { setError('Sélectionnez un fichier.'); return; }
    if (!titre.trim()) { setError('Donnez un titre au support.'); return; }

    const fd = new FormData();
    fd.append('fichier', fichier);
    fd.append('titre', titre.trim());
    if (description.trim()) fd.append('description', description.trim());
    if (matiereId) fd.append('matiere_id', String(matiereId));

    try {
      await uploader(fd).unwrap();
      onUploaded();
    } catch (err: any) {
      setError(err?.data?.error || err?.message || 'Erreur lors de l\'upload.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose}>
      <form onSubmit={handleSubmit}
        className="bg-gray-900 border border-gray-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 bg-gray-900 border-b border-gray-700 px-4 py-3 flex justify-between items-center">
          <h3 className="font-bold text-white inline-flex items-center gap-2">
            <Upload size={16} /> Nouveau support
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-200">
            <X size={18} />
          </button>
        </header>

        <div className="p-4 space-y-3">
          {/* Sélection matière */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
              Matière <span className="text-red-400">*</span>
            </label>
            <select value={matiereId} onChange={(e) => setMatiereId(e.target.value ? Number(e.target.value) : '')}
              className="w-full px-3 py-2 text-sm bg-gray-950 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500"
              required>
              <option value="">— Choisir une matière —</option>
              {(() => {
                // Groupage par semestre puis tri pair/impair pour clarté visuelle
                const parSem: Record<number, typeof matieres> = {};
                for (const m of matieres) {
                  const k = Number(m.semestre) || 0;
                  (parSem[k] ||= []).push(m);
                }
                const cles = Object.keys(parSem).map(Number).sort((a, b) => a - b);
                return cles.map(sem => {
                  const parite = sem % 2 === 1 ? 'impair' : 'pair';
                  const label = sem === 0
                    ? 'Sans semestre'
                    : `Semestre ${sem} (${parite})`;
                  return (
                    <optgroup key={sem} label={label}>
                      {parSem[sem]
                        .slice()
                        .sort((a, b) => a.nom_matiere.localeCompare(b.nom_matiere))
                        .map(m => (
                          <option key={m.matiere_id} value={m.matiere_id}>
                            {m.nom_matiere} — {m.niveau} ({m.option || '—'})
                          </option>
                        ))}
                    </optgroup>
                  );
                });
              })()}
            </select>
            {matieres.length === 0 && (
              <div className="text-[11px] text-amber-400 mt-1">
                Aucune matière trouvée. Demandez à la scolarité d'attribuer vos matières.
              </div>
            )}
          </div>

          {/* Sélection fichier */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
              Fichier <span className="text-red-400">*</span>
            </label>
            <input ref={inputRef} type="file" accept={EXTS_OK.map(e => `.${e}`).join(',')}
              onChange={handleFileChange}
              className="block w-full text-xs text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-blue-600 file:text-white file:font-semibold hover:file:bg-blue-700" />
            {fichier && (
              <div className="text-[11px] text-emerald-400 mt-1">
                ✓ {fichier.name} ({(fichier.size / 1024).toFixed(0)} Ko)
              </div>
            )}
            <div className="text-[10px] text-gray-500 mt-1">
              Formats : {EXTS_OK.join(', ')} • Max {TAILLE_MAX_MB} Mo
            </div>
          </div>

          {/* Titre */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
              Titre <span className="text-red-400">*</span>
            </label>
            <input value={titre} onChange={(e) => setTitre(e.target.value)}
              maxLength={200} required placeholder="Ex. Cours chapitre 1 — Algorithmes"
              className="w-full px-3 py-2 text-sm bg-gray-950 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500" />
          </div>

          {/* Description */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
              Description <span className="font-normal text-gray-500 normal-case">(optionnel)</span>
            </label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              rows={3} maxLength={5000}
              placeholder="Détails, prérequis, dates de référence…"
              className="w-full px-3 py-2 text-sm bg-gray-950 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 resize-none" />
          </div>

          {error && (
            <div className="bg-red-900/30 border border-red-800 text-red-200 rounded-lg p-2 text-sm">
              {error}
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-gray-900 border-t border-gray-700 px-4 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-2 text-xs text-gray-300 hover:bg-gray-800 rounded">
            Annuler
          </button>
          <button type="submit" disabled={isLoading || !fichier || !titre.trim() || !matiereId}
            className="px-4 py-2 text-xs font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded inline-flex items-center gap-1.5">
            {isLoading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {isLoading ? 'Upload…' : 'Uploader'}
          </button>
        </footer>
      </form>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────

function EditModal({ item, onClose, onSave }: {
  item: SupportCoursItem;
  onClose: () => void;
  onSave: (titre: string, description: string) => void;
}) {
  const [titre, setTitre] = useState(item.titre);
  const [description, setDescription] = useState(item.description);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 bg-gray-900 border-b border-gray-700 px-4 py-3 flex justify-between items-center">
          <h3 className="font-bold text-white">Modifier le support</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200">
            <X size={18} />
          </button>
        </header>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
              Titre
            </label>
            <input value={titre} onChange={(e) => setTitre(e.target.value)}
              maxLength={200}
              className="w-full px-3 py-2 text-sm bg-gray-950 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
              Description
            </label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              rows={4} maxLength={5000}
              className="w-full px-3 py-2 text-sm bg-gray-950 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 resize-none" />
          </div>
          <div className="text-[11px] text-gray-500">
            Fichier : <span className="text-gray-300">{item.nom_original}</span> ({item.taille_humaine})
          </div>
        </div>

        <footer className="sticky bottom-0 bg-gray-900 border-t border-gray-700 px-4 py-3 flex justify-end gap-2">
          <button onClick={onClose}
            className="px-3 py-2 text-xs text-gray-300 hover:bg-gray-800 rounded">
            Annuler
          </button>
          <button onClick={() => onSave(titre, description)}
            disabled={!titre.trim()}
            className="px-4 py-2 text-xs font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded">
            Enregistrer
          </button>
        </footer>
      </div>
    </div>
  );
}
